import { lazy, Suspense } from 'react';
import { VoileExport } from '@/components/shared/VoileExport';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ROUTES } from '@/lib/routes';
import { AppShell } from '@/components/layout/AppShell';
import { ProtectedRoute } from '@/components/shared/ProtectedRoute';
import { PageLoader } from '@/components/shared/PageLoader';

// ─── Lazy imports — pages auth ────────────────────────────────────────────────
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const TwoFactorPage = lazy(() => import('@/pages/TwoFactorPage'));
const TwoFactorEnrollPage = lazy(() => import('@/pages/TwoFactorEnrollPage'));   // `#551` C.3

// ─── Lazy imports — modules ───────────────────────────────────────────────────
// HomePage lazy removed — root now redirects to /alerts (PHASE 3.0 UX Revolution)
// const HomePage = lazy(() => import('@/pages/modules/HomePage')); // kept for reference
// MarcheLandingPage (/marche/home) supprimée en vague dettes 2 (2026-07-27) —
// contenu fusionné dans PhenomenaMarketPage (/phenomena/marche) qui porte
// désormais les contrôles marque + canal.
const MarcheRetailPage = lazy(() => import('@/pages/modules/MarcheRetailPage')); // page unifiée Retail / Wholesale (VF.REFONTE-MODULES-B)
const PerformancePage = lazy(() => import('@/pages/modules/PerformancePage'));
// PerformanceMerchPage retirée en P9 P5 — la fiche merch est désormais un
// drawer (merch360Store + MerchDetailDrawer). DECISIONS #285.
const ComparatorPage = lazy(() => import('@/pages/modules/ComparatorPage'));
const BrandVsBrandPage = lazy(() => import('@/pages/modules/BrandVsBrandPage'));
const OfficialLaunchesPage = lazy(() => import('@/pages/modules/OfficialLaunchesPage'));
const LabPage    = lazy(() => import('@/pages/modules/LabPage'));
const LabHubPage = lazy(() => import('@/pages/modules/LabHubPage'));
const GeoDataPage = lazy(() => import('@/pages/modules/GeoDataPage'));
const FlashInfoFeedPage = lazy(() => import('@/pages/modules/FlashInfoFeedPage'));
const FlashInfoCreatePage = lazy(() => import('@/pages/modules/FlashInfoCreatePage'));
const FlashInfoEditPage = lazy(() => import('@/pages/modules/FlashInfoEditPage'));
const ReferentielsPdvPage = lazy(() => import('@/pages/modules/ReferentielsPdvPage'));
const ReferentielsSkuPage = lazy(() => import('@/pages/modules/ReferentielsSkuPage'));
const ReferentielsBrandsPage = lazy(() => import('@/pages/modules/ReferentielsBrandsPage'));
const ReferentielsMerchandisersPage = lazy(() => import('@/pages/modules/ReferentielsMerchandisersPage'));
const ReferentielsRoutingsPage = lazy(() => import('@/pages/modules/ReferentielsRoutingsPage'));
// `#1091` — SEGMENTATION RÉTABLIE : masquée à tort au `#1072` (il fallait la vider, pas la cacher).
const AdminSegmentationPage = lazy(() => import('@/pages/modules/AdminSegmentationPage'))
const AdminSegmentationFactorsCataloguePage = lazy(
  () => import('@/pages/modules/AdminSegmentationFactorsCataloguePage'),
);
// `#1068` — Market Cluster masqué : import retiré avec sa route (code conservé sur disque).
// const AdminMarketClustersPage = lazy(() => import('@/pages/modules/AdminMarketClustersPage'));
const AdminImportPage = lazy(() => import('@/pages/modules/AdminImportPage'));
const AdminImportHistoryPage = lazy(() => import('@/pages/modules/AdminImportHistoryPage'));
const AdminReconciliationReportPage = lazy(
  () => import('@/pages/modules/AdminReconciliationReportPage')
);
const AdminValidationSkusPage = lazy(() => import('@/pages/modules/AdminValidationSkusPage'));
const AdminValidationPdvsPage = lazy(() => import('@/pages/modules/AdminValidationPdvsPage'));
const AdminValidationMerchPage   = lazy(() => import('@/pages/modules/AdminValidationMerchPage'));
const AdminValidationBrandsPage  = lazy(() => import('@/pages/modules/AdminValidationBrandsPage'));
const AdminValidationOrphansPage = lazy(() => import('@/pages/modules/AdminValidationOrphansPage'));
const ParametresPage = lazy(() => import('@/pages/modules/ParametresPage')); // #14bis fusion 3->1
const AdminAlertConfigPage = lazy(() => import('@/pages/modules/AdminAlertConfigPage')); // V4.20.1
const AdminPerfAlertConfigPage = lazy(() => import('@/pages/modules/AdminPerfAlertConfigPage')); // P9 Palier 4
const AdminAuditLogsPage = lazy(() => import('@/pages/modules/AdminAuditLogsPage'));
const AdminMonitoringPage = lazy(() => import('@/pages/modules/AdminMonitoringPage')); // Phase δ DECISIONS #163
const AlertingPage = lazy(() => import('@/modules/alerting/pages/AlertingPage').then(m => ({ default: m.AlertingPage }))); // F1 DECISIONS #166 PHASE 2

// ─── Lazy imports — Data Quality (post P3 — Pilotage seule page restante) ──
const DataQualityPilotagePage       = lazy(() => import('@/modules/data-quality/pages/DataQualityPilotagePage'));

// ─── Lazy imports — Alertes Landing (PHASE 3.0 UX Revolution DECISIONS #167) ─
const AlertsLandingPage = lazy(() => import('@/modules/alerts/pages/AlertsLandingPage'));

// ─── Lazy imports — Phénomènes P1 (V4.20 module dédié) ──────────────────────
const PhenomenaMarketPage = lazy(() => import('@/modules/phenomena/pages/PhenomenaMarketPage'));
// #12+13 P1.1 — Centre de notifications. Aucun lien n'y mène encore (cf. routes.ts).
const NotificationsPage = lazy(() => import('@/pages/modules/NotificationsPage'));
// V4.22 / B2 — Drill basculé en POP-UP (PhenomenaDrillModal géré par état React au-dessus
// de la page Marché). La route /phenomena/marche/:id/drill (sous-bulle 1) est retirée.

/**
 * AppRouter — configuration complète React Router v6.
 *
 * Stratégie :
 * - Routes publiques : /login, /2fa
 * - Routes protégées : AppShell > ProtectedRoute > page (lazy + Suspense)
 * - Certaines routes requièrent une permission spécifique (ex: admin, settings)
 * - / → redirect vers /marche (URL canonique Retail / Wholesale — VF.REFONTE-MODULES-B)
 */
export function AppRouter() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      {/**
        * ⚠⚠ `#656` — **LE VOILE EST MONTÉ ICI, UNE FOIS, AU-DESSUS DES ROUTES.**
        *   *Il vivait dans la page Marché : il se démontait à la première
        *   navigation de l'export, et depuis que `F11` navigue au premier geste
        *   (`#652`), il s'évanouissait presque aussitôt.* Ici, aucune route ne
        *   peut l'emporter — c'est la propriété « visible même quand on va sur
        *   une autre page » de la spec Hkm du 29/08.
        */}
      <VoileExport />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ── Public ─────────────────────────────────────────────────────── */}
          <Route path={ROUTES.login} element={<LoginPage />} />
          <Route path={ROUTES.twoFactor} element={<TwoFactorPage />} />
          <Route path={ROUTES.twoFactorEnroll} element={<TwoFactorEnrollPage />} />

          {/* ── Authentifié — AppShell ──────────────────────────────────────── */}
          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            {/* Root → /alerts (PHASE 3.0 UX Revolution — nouvelle landing page) */}
            <Route
              index
              path={ROUTES.root}
              element={<Navigate to={ROUTES.welcome} replace />}
            />

            {/* Welcome (ex-Alertes Landing) — PHASE 3.0 UX Revolution (DECISIONS #167) */}
            <Route path={ROUTES.welcome} element={<AlertsLandingPage />} />
            {/* Compat redirect — ancienne URL /alerts → /welcome */}
            <Route path="/alerts" element={<Navigate to={ROUTES.welcome} replace />} />

            {/* Phénomènes Marché (V4.20) — RBAC market.view (HasPermission dans la page) */}
            <Route
              path={ROUTES.phenomenaMarche}
              element={
                <ProtectedRoute requirePermission="analyse.voir">
                  <PhenomenaMarketPage />
                </ProtectedRoute>
              }
            />
            {/* V4.22 / B2 — Drill cascade GEO basculé en pop-up (PhenomenaDrillModal géré
                par état dans PhenomenaAlertsView). Route /drill retirée — la page Marché
                reste montée derrière le modal flouté. Deep-link ?drill=<id> = backlog. */}

            {/* Centre de notifications — #12+13 P1.1. ⚠ Page NOUVELLE, aucun lien
                ajouté ailleurs : la landing reste intouchable et la nav ne bouge pas.
                L'accès direct par URL suffit à ce palier ; la cloche arrive en P1.6. */}
            <Route
              path={ROUTES.notifications}
              element={
                <ProtectedRoute requirePermission="notifications.voir">
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />

            {/* Vague dettes 2 (2026-07-27) — /marche/home → /phenomena/marche.
                Compat bookmarks/liens externes. La page cible porte désormais
                les contrôles marque + canal fusionnés depuis l'ex-landing. */}
            <Route path="/marche/home" element={<Navigate to={ROUTES.phenomenaMarche} replace />} />

            {/* Marché — URL canonique /marche (VF.REFONTE-MODULES-B DECISIONS #153) */}
            <Route
              path={ROUTES.marche}
              element={
                <ProtectedRoute requirePermission="analyse.voir">
                  <MarcheRetailPage />
                </ProtectedRoute>
              }
            />
            {/* Redirects compat D32 — preserve bookmarks / liens existants */}
            <Route path={ROUTES.marcheRetail} element={<Navigate to={ROUTES.marche} replace />} />
            <Route path="/marche/wholesale" element={<Navigate to={`${ROUTES.marche}?channel=wholesale`} replace />} />

            {/* Performance */}
            <Route
              path={ROUTES.performance}
              element={
                <ProtectedRoute requirePermission="analyse.voir">
                  <PerformancePage />
                </ProtectedRoute>
              }
            />
            {/* Route /performance/merch/:code retirée en P9 P5 — remplacée par
                drawer (merch360Store). DECISIONS #285. */}

            {/* Comparateur */}
            <Route
              path={ROUTES.comparator}
              element={
                <ProtectedRoute requirePermission="analyse.voir">
                  <ComparatorPage />
                </ProtectedRoute>
              }
            />

            {/* Lancements officiels (S10 P2) — module autonome.
                ⚠ N'atterrit JAMAIS sur du vide : Samsung n'a aucun lancement et
                son état vide est l'écran nominal, avec le sélecteur de marque
                juste à côté pour aller voir les concurrents. */}
            <Route
              path={ROUTES.officialLaunches}
              element={
                <ProtectedRoute requirePermission="analyse.voir">
                  <OfficialLaunchesPage />
                </ProtectedRoute>
              }
            />

            {/* Brand vs Brand — placeholder */}
            <Route
              path={ROUTES.brandVsBrand}
              element={
                <ProtectedRoute requirePermission="analyse.voir">
                  <BrandVsBrandPage />
                </ProtectedRoute>
              }
            />

            {/* Self Service Lab */}
            <Route
              path={ROUTES.lab}
              element={
                <ProtectedRoute requirePermission="analyse.voir">
                  <LabPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.labHub}
              element={
                <ProtectedRoute requirePermission="analyse.voir">
                  <LabHubPage />
                </ProtectedRoute>
              }
            />

            {/* Geo-data (VF.GEO.1) */}
            <Route
              path={ROUTES.geoData}
              element={
                <ProtectedRoute requirePermission="analyse.voir">
                  <GeoDataPage />
                </ProtectedRoute>
              }
            />

            {/* Flash Info */}
            <Route
              path={ROUTES.flashinfoFeed}
              element={
                <ProtectedRoute requirePermission="flashinfo.voir">
                  <FlashInfoFeedPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.flashinfoCreate}
              element={
                <ProtectedRoute requirePermission="flashinfo.gerer">
                  <FlashInfoCreatePage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.flashinfoEditPattern}
              element={
                <ProtectedRoute requirePermission="flashinfo.gerer">
                  <FlashInfoEditPage />
                </ProtectedRoute>
              }
            />
            {/*
              ⚠ ROUTE DÉTAIL `/flashinfo/:id` SUPPRIMÉE — décision Hkm, franche.
                La fiche s'ouvre en overlay sur le feed (`?carte=<id>`).
              ⚠ CONSÉQUENCE ASSUMÉE : les anciens liens ne rendent pas une 404 ;
                ils tombent dans le catch-all `path="*"` plus bas et atterrissent
                SUR L'ACCUEIL, SANS MESSAGE. Connu, mesuré, accepté.
            */}

            {/* Référentiels */}
            <Route
              path={ROUTES.referentielsPdv}
              element={
                <ProtectedRoute requirePermission="referentiels.voir">
                  <ReferentielsPdvPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.referentielsSku}
              element={
                <ProtectedRoute requirePermission="referentiels.voir">
                  <ReferentielsSkuPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.referentielsBrands}
              element={
                <ProtectedRoute requirePermission="analyse.voir">
                  <ReferentielsBrandsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.referentielsMerchandisers}
              element={
                <ProtectedRoute requirePermission="performance.voir">
                  <ReferentielsMerchandisersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.referentielsRoutings}
              element={
                <ProtectedRoute requirePermission={['referentiels.voir', 'performance.voir']}>
                  <ReferentielsRoutingsPage />
                </ProtectedRoute>
              }
            />

            {/* Admin */}
            {/* `#1091` — SEGMENTATION RÉTABLIE (masquée à tort au `#1072` : la base
                a été vidée, la page reste — voir tous, gérer = Director, PM, Samsung PM). */}
            <Route
              path={ROUTES.adminSegmentation}
              element={
                <ProtectedRoute requirePermission="segmentation.voir">
                  <AdminSegmentationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.adminSegmentationFactorsCatalogue}
              element={
                <ProtectedRoute requirePermission="segmentation.voir">
                  <AdminSegmentationFactorsCataloguePage />
                </ProtectedRoute>
              }
            />
            {/* `#1068` — MARKET CLUSTER MASQUÉ (menu + route) : plus atteignable
                depuis l'app ; le code (page/modals) reste pour une suppression
                ultérieure au ticket dédié.
            <Route
              path={ROUTES.adminMarketClusters}
              element={
                <ProtectedRoute requirePermission="referentiels.voir">
                  <AdminMarketClustersPage />
                </ProtectedRoute>
              }
            /> */}

            {/* Admin — Import pipeline */}
            <Route
              path={ROUTES.adminImport}
              element={
                <ProtectedRoute requirePermission="imports.gerer">
                  <AdminImportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.adminImportHistory}
              element={
                <ProtectedRoute requirePermission="imports.voir">
                  <AdminImportHistoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.adminImportReportPattern}
              element={
                <ProtectedRoute requirePermission="imports.gerer">
                  <AdminReconciliationReportPage />
                </ProtectedRoute>
              }
            />

            {/* Admin — Validation référentiels (4.J.2) */}
            <Route
              path={ROUTES.adminValidationSkus}
              element={
                <ProtectedRoute requirePermission="validation.voir">
                  <AdminValidationSkusPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.adminValidationPdvs}
              element={
                <ProtectedRoute requirePermission="validation.voir">
                  <AdminValidationPdvsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.adminValidationMerch}
              element={
                <ProtectedRoute requirePermission="merch.identite_voir">
                  <AdminValidationMerchPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.adminValidationBrands}
              element={
                <ProtectedRoute requirePermission="validation.voir">
                  <AdminValidationBrandsPage />
                </ProtectedRoute>
              }
            />
              <Route
                path={ROUTES.adminValidationOrphans}
                element={
                  <Suspense fallback={<PageLoader />}>
                    <AdminValidationOrphansPage />
                  </Suspense>
                }
              />

            {/* #14bis — FUSION : page unique « Paramètres » a onglets. */}
            <Route
              path={ROUTES.parametres}
              element={
                // ⚠ ENTREE = UNION des trois gardes. Chaque onglet filtre ensuite
                //   par SA permission (ParametresPage). Un mono-permission entre
                //   et ne voit que sa section.
                <ProtectedRoute requirePermission={['configuration.modifier', 'configuration.modifier', 'utilisateurs.voir']}>
                  <ParametresPage />
                </ProtectedRoute>
              }
            />
            {/* ⚠ ANCIENNES ROUTES -> REDIRECTIONS vers le bon onglet (les liens
                d'hier menent encore juste). */}
            <Route path={ROUTES.adminUsers} element={<Navigate to={`${ROUTES.parametres}?tab=utilisateurs`} replace />} />
            <Route path={ROUTES.adminConfig} element={<Navigate to={`${ROUTES.parametres}?tab=configuration`} replace />} />
            <Route
              path={ROUTES.adminAlertConfig}
              element={
                // V4.20 — Accès route gated `phenomenes.seuils` (SA + ADMIN_ORBICORE).
                // La branche RO du composant (HasPermission interne) devient inerte
                // — conservée en filet si on rouvre la lecture aux non-admins plus tard.
                <ProtectedRoute requirePermission="configuration.voir">
                  <AdminAlertConfigPage />
                </ProtectedRoute>
              }
            />

            {/* P9 Palier 4 — Seuils alertes performance */}
            <Route
              path={ROUTES.adminPerfAlertConfig}
              element={
                <ProtectedRoute requirePermission="configuration.voir">
                  <AdminPerfAlertConfigPage />
                </ProtectedRoute>
              }
            />

            {/* Data Quality — post P3 (2026-07-25) : Pilotage seule page.
                /data-quality sert Pilotage directement (plus de redirect).
                /data-quality/pilotage conservé comme alias pour deep-links existants. */}
            <Route
              path={ROUTES.dataQuality}
              element={
                <ProtectedRoute requirePermission="analyse.voir">
                  <DataQualityPilotagePage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.dataQualityPilotage}
              element={
                <ProtectedRoute requirePermission="analyse.voir">
                  <DataQualityPilotagePage />
                </ProtectedRoute>
              }
            />

            {/* Admin — Audit Logs (4.K.1) */}
            <Route
              path={ROUTES.adminAuditLogs}
              element={
                <ProtectedRoute requirePermission="supervision.voir">
                  <AdminAuditLogsPage />
                </ProtectedRoute>
              }
            />

            {/* Admin — Monitoring système (Phase δ DECISIONS #163) */}
            <Route
              path={ROUTES.adminMonitoring}
              element={
                <ProtectedRoute requirePermission="supervision.voir">
                  <AdminMonitoringPage />
                </ProtectedRoute>
              }
            />

            {/* Admin — Alertes système F1 (DECISIONS #166 PHASE 2) */}
            <Route
              path={ROUTES.adminAlerting}
              element={
                <ProtectedRoute requirePermission="supervision.voir">
                  <AlertingPage />
                </ProtectedRoute>
              }
            />

            {/* Settings — #14bis : /settings redirige vers l'onglet Parametres. */}
            <Route path={ROUTES.settings} element={<Navigate to={`${ROUTES.parametres}?tab=parametres`} replace />} />

          </Route>

          {/* ── Fallback — catch-all ────────────────────────────────────────── */}
          <Route path="*" element={<Navigate to={ROUTES.welcome} replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
